import { useEffect, useState } from "react";
import { css, cx } from "styled-system/css";
import { format } from "date-fns";
import { es } from "date-fns/locale/es";
import { ChevronDown, FileText, ImageIcon, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/providers/AuthProvider";
import { useConfirm } from "@/hooks/useConfirm";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import {
  ODONTOGRAM_IMAGE_CATEGORIES,
  ODONTOGRAM_IMAGE_CATEGORY_LABELS,
  isPdfImage,
  type OdontogramImage,
  type OdontogramImageCategory,
} from "./odontogram-image-categories";
import { OdontogramImageUploadModal } from "./odontogram-image-upload-modal";

function formatTakenAt(img: OdontogramImage): string | null {
  const raw = img.takenAt ?? img.createdAt;
  if (!raw) return null;
  try {
    return format(new Date(raw), "d MMM yyyy", { locale: es });
  } catch {
    return null;
  }
}

function ImageTile({ img, onOpen, onDelete }: { img: OdontogramImage; onOpen: () => void; onDelete: () => void }) {
  const pdf = isPdfImage(img);
  return (
    <div
      className={css({
        position: "relative",
        rounded: "lg",
        overflow: "hidden",
        borderWidth: "1px",
        borderColor: "#e2e8f0",
        bg: "#f8fafc",
        cursor: "pointer",
        "&:hover .odon-img-tile-actions": { opacity: 1 },
      })}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label="Ver imagen"
        className={css({ display: "block", w: "100%", border: "none", p: 0, bg: "transparent", cursor: "pointer" })}
      >
        {pdf ? (
          <div
            className={css({
              aspectRatio: "1 / 1",
              display: "flex",
              flexDir: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "1.5",
              color: "#94a3b8",
            })}
          >
            <FileText size={28} aria-hidden />
            <span className={css({ fontSize: "xs", fontWeight: "medium" })}>PDF</span>
          </div>
        ) : (
          <img
            src={img.cloudinaryUrl}
            alt={img.filename ?? ODONTOGRAM_IMAGE_CATEGORY_LABELS[img.category]}
            loading="lazy"
            className={css({
              aspectRatio: "1 / 1",
              width: "100%",
              objectFit: "cover",
              display: "block",
              transition: "transform 200ms cubic-bezier(0.23, 1, 0.32, 1)",
              _hover: { transform: "scale(1.04)" },
            })}
          />
        )}
      </button>

      <div
        className={css({
          px: "2",
          py: "1.5",
          fontSize: "xs",
          color: "#64748b",
          borderTopWidth: "1px",
          borderColor: "#e2e8f0",
          bg: "white",
        })}
      >
        {formatTakenAt(img) ?? "—"}
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        aria-label="Eliminar imagen"
        className={cx(
          "odon-img-tile-actions",
          css({
            position: "absolute",
            top: "1.5",
            right: "1.5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            w: "7",
            h: "7",
            rounded: "md",
            bg: "rgba(15,23,42,0.72)",
            color: "white",
            border: "none",
            cursor: "pointer",
            opacity: 0,
            transition: "opacity 150ms ease, background-color 120ms ease",
            _hover: { bg: "#dc2626" },
          }),
        )}
      >
        <Trash2 size={13} aria-hidden />
      </button>
    </div>
  );
}

function ImageLightbox({ img, onClose, onDelete }: { img: OdontogramImage; onClose: () => void; onDelete: () => void }) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const pdf = isPdfImage(img);

  return (
    <div
      role="dialog"
      aria-modal
      onClick={(e) => e.target === e.currentTarget && onClose()}
      className={css({
        position: "fixed",
        inset: 0,
        bg: "rgba(15,23,42,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 60,
        p: "4",
      })}
    >
      <div
        className={css({
          bg: "white",
          rounded: "xl",
          overflow: "hidden",
          maxW: "3xl",
          w: "100%",
          maxH: "calc(100vh - 2rem)",
          display: "flex",
          flexDir: "column",
        })}
      >
        <div
          className={css({
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "3",
            px: "4",
            py: "3",
            borderBottomWidth: "1px",
            borderColor: "#e2e8f0",
          })}
        >
          <span className={css({ fontSize: "sm", fontWeight: "bold", color: "#0f172a" })}>
            {ODONTOGRAM_IMAGE_CATEGORY_LABELS[img.category]}
          </span>
          <div className={css({ display: "flex", alignItems: "center", gap: "1" })}>
            <button
              type="button"
              onClick={onDelete}
              aria-label="Eliminar imagen"
              className={css({
                display: "flex",
                alignItems: "center",
                gap: "1",
                fontSize: "xs",
                color: "#ef4444",
                bg: "transparent",
                border: "none",
                cursor: "pointer",
                px: "2",
                py: "1.5",
                rounded: "md",
                _hover: { bg: "#fef2f2" },
              })}
            >
              <Trash2 size={14} aria-hidden />
              Eliminar
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              className={css({ display: "flex", bg: "transparent", border: "none", cursor: "pointer", color: "#64748b", p: "1.5", rounded: "md", _hover: { bg: "#f1f5f9" } })}
            >
              <X size={18} aria-hidden />
            </button>
          </div>
        </div>

        <div className={css({ overflow: "auto", bg: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", flex: 1, minH: "200px" })}>
          {pdf ? (
            <a
              href={img.cloudinaryUrl}
              target="_blank"
              rel="noreferrer"
              className={css({ color: "white", fontSize: "sm", display: "flex", flexDir: "column", alignItems: "center", gap: "2", py: "10" })}
            >
              <FileText size={40} aria-hidden />
              Abrir PDF en una pestaña nueva
            </a>
          ) : (
            <img src={img.cloudinaryUrl} alt={img.filename ?? ""} className={css({ maxW: "100%", maxH: "70vh", objectFit: "contain" })} />
          )}
        </div>

        <div className={css({ px: "4", py: "3", display: "flex", flexDir: "column", gap: "1.5" })}>
          <p className={css({ fontSize: "xs", color: "#64748b" })}>
            Fecha: <span className={css({ color: "#0f172a", fontWeight: "medium" })}>{formatTakenAt(img) ?? "Sin fecha"}</span>
          </p>
          {img.notes ? (
            <p className={css({ fontSize: "sm", color: "#374151", whiteSpace: "pre-line" })}>{img.notes}</p>
          ) : (
            <p className={css({ fontSize: "xs", color: "#94a3b8" })}>Sin notas.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export function OdontogramImagesPanel({ clientId }: { clientId: string }) {
  const { refresh } = useAuth();
  const confirm = useConfirm();
  const [images, setImages] = useState<OdontogramImage[] | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [lightboxId, setLightboxId] = useState<string | null>(null);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<OdontogramImageCategory>>(() => new Set());

  function toggleCategory(cat: OdontogramImageCategory) {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }

  async function load() {
    const r = await fetchWithSessionSync(
      `/api/dashboard/odontogram/images/${clientId}`,
      { credentials: "include" },
      refresh,
    );
    if (!r.ok) {
      setImages([]);
      return;
    }
    const data = (await r.json()) as { images: OdontogramImage[] };
    setImages(data.images);
  }

  useEffect(() => {
    setImages(null);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  async function handleDelete(id: string) {
    const ok = await confirm({
      message: "¿Eliminar esta imagen? Esta acción no se puede deshacer.",
      tone: "danger",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    const r = await fetchWithSessionSync(
      `/api/dashboard/odontogram/images/${id}`,
      { method: "DELETE", credentials: "include" },
      refresh,
    );
    if (r.ok) {
      setImages((prev) => (prev ? prev.filter((i) => i.id !== id) : prev));
      setLightboxId((cur) => (cur === id ? null : cur));
    }
  }

  // El diálogo de confirm (useConfirm) comparte el mismo z-index que otros modales del sistema,
  // por debajo del lightbox (que necesita tapar hasta el header sticky del editor). Cerramos el
  // lightbox antes de disparar el confirm para que no quede atrapado detrás de su overlay.
  async function handleDeleteFromLightbox(id: string) {
    setLightboxId(null);
    await handleDelete(id);
  }

  const lightboxImg = images?.find((i) => i.id === lightboxId) ?? null;

  return (
    <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
      <div className={css({ display: "flex", justifyContent: "flex-end" })}>
        <Button size="sm" onClick={() => setUploadOpen(true)}>
          <Plus size={14} aria-hidden />
          Subir imagen
        </Button>
      </div>

      {images === null ? (
        <div className={css({ py: "8", display: "flex", justifyContent: "center" })}>
          <span className={css({ fontSize: "sm", color: "#94a3b8" })}>Cargando…</span>
        </div>
      ) : images.length === 0 ? (
        <div className={css({ py: "8", display: "flex", flexDir: "column", alignItems: "center", gap: "2", color: "#94a3b8" })}>
          <ImageIcon size={28} aria-hidden />
          <p className={css({ fontSize: "sm", textAlign: "center" })}>Aun no hay imágenes cargadas.</p>
          <p className={css({ fontSize: "xs", textAlign: "center" })}>Subí radiografías, fotos o documentos con el botón de arriba.</p>
        </div>
      ) : (
        <div className={css({ display: "flex", flexDir: "column", gap: "5" })}>
          {ODONTOGRAM_IMAGE_CATEGORIES.map((cat) => {
            const inCat = images.filter((i) => i.category === cat);
            if (inCat.length === 0) return null;
            const isOpen = !collapsedCategories.has(cat);
            return (
              <div key={cat} className={css({ display: "flex", flexDir: "column", gap: "2" })}>
                <button
                  type="button"
                  onClick={() => toggleCategory(cat)}
                  aria-expanded={isOpen}
                  className={css({
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "2",
                    bg: "transparent",
                    border: "none",
                    p: 0,
                    cursor: "pointer",
                    textAlign: "left",
                    color: "inherit",
                    "&:hover h3": { color: "#334155" },
                  })}
                >
                  <h3 className={css({ fontSize: "xs", fontWeight: "bold", color: "#0f172a", textTransform: "uppercase", letterSpacing: "wide", transition: "color 120ms ease" })}>
                    {ODONTOGRAM_IMAGE_CATEGORY_LABELS[cat]}
                    <span className={css({ fontWeight: "normal", color: "#94a3b8", ml: "1.5", textTransform: "none", letterSpacing: "normal" })}>
                      ({inCat.length})
                    </span>
                  </h3>
                  <ChevronDown
                    size={16}
                    aria-hidden
                    className={css({ color: "#94a3b8", flexShrink: 0, transition: "transform 160ms cubic-bezier(0.23, 1, 0.32, 1)" })}
                    style={{ transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)" }}
                  />
                </button>
                <div
                  className={css({
                    display: "grid",
                    gridTemplateRows: isOpen ? "1fr" : "0fr",
                    transition: "grid-template-rows 200ms cubic-bezier(0.23, 1, 0.32, 1)",
                  })}
                >
                  <div className={css({ overflow: "hidden", minH: 0 })}>
                    <div className={css({ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: "2.5", pt: "0.5" })}>
                      {inCat.map((img) => (
                        <ImageTile key={img.id} img={img} onOpen={() => setLightboxId(img.id)} onDelete={() => void handleDelete(img.id)} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <OdontogramImageUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        clientId={clientId}
        onUploaded={() => void load()}
      />

      {lightboxImg && (
        <ImageLightbox
          img={lightboxImg}
          onClose={() => setLightboxId(null)}
          onDelete={() => void handleDeleteFromLightbox(lightboxImg.id)}
        />
      )}
    </div>
  );
}
